<template>
  <div
    v-click-outside="
      () => {
        isDropdownOpen = false
      }
    "
    class="relative"
  >
    <div
      class="flex cursor-pointer items-center whitespace-nowrap border-1px border-primary-10 rounded-10px bg-light-gray px-16px py-8px"
      @click="isDropdownOpen = !isDropdownOpen"
    >
      <LoadingSpinner
        v-if="isCatalogLoading || productState?.selectedProduct === null || productState === null"
        class="flex-1"
        is-loading
      />
      <p v-else class="flex flex-1 items-center gap-16px">
        <span class="title-12-h24-m text-primary-80">{{ $t('panel.submenu.accessories') }}</span
        ><span class="content-12-h24-r text-primary-60">{{
          `${productState.selectedProduct.title.product} (${productState.selectedProduct.title.version})`
        }}</span>
        <AuColorDisplay
          :color-type="productState.selectedProduct.variant.color.type"
          :value="productState.selectedProduct.variant.color.value"
        />
      </p>
      <i class="icon-chevron-down text-24px text-primary-80" />
    </div>
    <div
      v-if="isDropdownOpen"
      class="absolute bottom-0 z-1 w-full translate-y-full border-1px border-primary-30 rounded-10px bg-white p-8px text-primary-80 shadow-dropdown"
    >
      <template v-if="stackMenu.length === 0">
        <p class="options-title">{{ $t('dropdown.product') }}</p>
        <p
          class="option"
          :class="{ '!hover:bg-white !cursor-default': isSingleProductSetGroup }"
          @click="!isSingleProductSetGroup && selectProductSetGroup()"
        >
          <span class="flex-1">{{ $t(`catalog.${productState?.categoryType}.product`) }}</span>
          <span class="whitespace-pre text-right content-12-h24-r text-primary-60 -my-12px">{{
            `${productState?.selectedProduct?.title.product}\n${productState?.selectedProduct?.title.version}`
          }}</span>
          <i
            class="icon-chevron-right ml-4px text-24px -my-4px"
            :class="{ 'opacity-0': isSingleProductSetGroup }"
          />
        </p>
        <p
          class="option"
          :class="{ '!hover:bg-white !cursor-default': isSingleVariant }"
          @click="!isSingleVariant && selectVariant()"
        >
          <span class="flex-1">{{
            $t(`catalog.${productState?.categoryType}.product-color`)
          }}</span>
          <AuColorDisplay
            v-if="productState?.selectedProduct?.variant.color"
            :color-type="productState?.selectedProduct?.variant.color.type"
            :value="productState?.selectedProduct?.variant.color.value"
          />
          <span class="ml-8px content-12-h24-r text-primary-60">{{
            productState?.selectedProduct?.variant.color.name
          }}</span>
          <i
            class="icon-chevron-right ml-4px text-24px -my-4px"
            :class="{ 'opacity-0': isSingleVariant }"
          />
        </p>
      </template>
      <template v-if="expandedProductSetGroup !== null">
        <p class="options-title w-fit cursor-pointer" @click="expandedProductSetGroup = null">
          <i class="icon-chevron-left mr-12px text-24px" />{{
            $t(`product-set.${expandedProductSetGroup.groupKey}`)
          }}
        </p>
        <div
          v-for="(productSet, productSetIndex) in expandedProductSetGroup.productSets"
          :key="productSetIndex"
        >
          <p class="py-4px content-11-h16-r text-primary-50">
            {{ $t(`product-set.${productSet.handle}`) }}
          </p>
          <div
            v-for="(product, productIndex) in productSet.productOption"
            :key="productIndex"
            class="option"
            @click="changeProduct(product.handle)"
          >
            {{ $t(`version.${product.version}`) }}
          </div>
        </div>
      </template>
      <template v-else-if="lastStack !== undefined">
        <p class="options-title w-fit cursor-pointer" @click="stackMenu.pop()">
          <i class="icon-chevron-left mr-12px text-24px" /> {{ lastStack.title }}
        </p>
        <div class="scrollbar max-h-340px overflow-y-auto">
          <template v-if="isColorItem(lastStack)">
            <div
              v-for="(item, index) in lastStack.options"
              :key="index"
              class="option gap-8px"
              :class="{
                'pointer-events-none':
                  'stockStatus' in item && item.stockStatus === StockStatus.Pending,
              }"
              @click="lastStack.onClick?.(item.value ?? '')"
            >
              <AuColorDisplay
                :class="{
                  'opacity-30': 'stockStatus' in item && item.stockStatus === StockStatus.Pending,
                }"
                :color-type="item.colorType"
                :value="item.colorDisplay"
              />
              <span
                class="content-12-h24-r text-primary-60"
                :class="{
                  'opacity-30': 'stockStatus' in item && item.stockStatus === StockStatus.Pending,
                }"
                >{{ item.name }}</span
              >
              <template v-if="'stockStatus' in item">
                <div class="tag" :class="getStockStatusTagClasses(item.stockStatus)">
                  {{ $t(`stock-status.${item.stockStatus}`) }}
                </div>
              </template>
              <i
                v-if="productState?.selectedProduct?.variant.sku === item.value"
                class="icon-check ml-auto text-24px"
              />
            </div>
          </template>
          <template v-else-if="isProductItem(lastStack)">
            <div
              v-for="(groupItem, productSetGroupIndex) in lastStack.productSetGroups"
              :key="`productSetGroup${productSetGroupIndex}`"
              class="option"
              :class="{
                '!hover:bg-white !cursor-default': isEmptyProductSet(groupItem.productSetGroup),
              }"
              @click="handleProductItemExpand(groupItem.productSetGroup)"
            >
              <img
                :alt="groupItem.thumbnail"
                class="h-40px w-40px"
                :class="{
                  'opacity-30': isEmptyProductSet(groupItem.productSetGroup),
                }"
                :src="groupItem.thumbnail"
              />
              <div class="ml-8px flex flex-col gap-4px">
                <span
                  :class="{
                    'opacity-30': isEmptyProductSet(groupItem.productSetGroup),
                  }"
                >
                  {{ $t(`product-set.${groupItem.productSetGroup.groupKey}`) }}
                </span>
                <span
                  v-if="isEmptyProductSet(groupItem.productSetGroup)"
                  class="tag whitespace-nowrap bg-primary-10 text-primary-50"
                  >{{ $t('tag.un_support') }}</span
                >
                <span
                  v-else-if="isSingleProduct(groupItem.productSetGroup)"
                  class="tag bg-primary-10 text-primary-50"
                  >{{
                    $t(
                      `version.${groupItem.productSetGroup.productSets[0]?.productOption[0]?.version}`,
                    )
                  }}</span
                >
              </div>
              <i
                v-if="
                  !isSingleProduct(groupItem.productSetGroup) &&
                  groupItem.productSetGroup.productSets.length !== 0
                "
                class="icon-chevron-right ml-auto text-24px text-primary-60"
              />
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AuColorDisplay } from '@evolutivelabs/amuse-ui'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import {
  isColorItem,
  isProductItem,
  type ProductSetGroup,
} from '@/components/newProductPanel/newProductPanel.type'
import {
  getStockStatusTagClasses,
  isSingleProduct,
  useProductSelector,
} from '@/components/newProductPanel/useProductSelector'
import { StockStatus } from '@/graphql/creator-platform/generated'
import { useNewProductStore } from '@/store/newProductStore'

const newProductStore = useNewProductStore()
const { productState } = storeToRefs(useNewProductStore())
const isDropdownOpen = ref(false)
const expandedProductSetGroup = ref<null | ProductSetGroup>(null)
const { stackMenu, changeProduct, selectProductSetGroup, selectVariant } = useProductSelector(reset)

const isCatalogLoading = computed(() => newProductStore.catalog === null)

const lastStack = computed(() => {
  return stackMenu.value[stackMenu.value.length - 1]
})

watch(
  () => isDropdownOpen.value,
  (newValue) => {
    if (!newValue) reset()
  },
)
function isEmptyProductSet(productSetGroup: ProductSetGroup): boolean {
  return productSetGroup.productSets.length === 0
}

function handleProductItemExpand(productSetGroup: ProductSetGroup): void {
  if (isEmptyProductSet(productSetGroup)) return
  if (isSingleProduct(productSetGroup)) {
    const handle = productSetGroup.productSets[0]?.productOption[0]?.handle
    if (handle === undefined) return
    changeProduct(handle)
    return
  }
  expandedProductSetGroup.value = productSetGroup
}

function reset(): void {
  stackMenu.value = []
  expandedProductSetGroup.value = null
  isDropdownOpen.value = false
}

const isSingleVariant = computed(
  () => productState.value?.selectedProduct?.product.variants.length === 1,
)

const isSingleProductSetGroup = computed(
  () => productState.value?.selectedProduct?.productSetGroups.length === 1,
)
</script>

<style scoped>
.options-title {
  @apply flex items-center px-8px py-12px content-12-h16-r text-primary-80;
}
.option {
  @apply flex cursor-pointer items-center rounded-5px px-8px py-12px content-12-h16-r hover:bg-light-gray;
}
.tag {
  @apply rounded-full px-8px py-4px content-10-h12-r w-fit;
}
</style>
